import { useCallback } from "react"

import { useActiveOrganizationId } from "@/contexts"
import type { Organization } from "@/models/organization"
import { AdminPath, RootPath } from "@/models/route"

export const getAuthenticatedRoutePath = (
  organizationId: Organization["id"],
  path: string
): string => {
  return `/${organizationId}/${path}`
}

interface UseUrlValue {
  /**
   * Builds a reporting url taking into account the active organization
   *
   * @param report The report
   * @returns A reporting url
   *
   * @example
   * const url = buildReportingUrl("emissions-sources")
   */
  buildReportingUrl: (report: string) => string

  /**
   * Builds a site url taking into account the active organization
   *
   * @param siteId The site id
   * @param tab The active tab
   * @returns A site url
   *
   * @example
   * const url = buildSitesUrl("1")
   * @example
   * const url = buildSitesUrl("1", "overview")
   */
  buildSiteUrl: (siteId: string, tab?: string) => string

  /**
   * Builds a sites url taking into account the active organization
   *
   * @param tab The active tab
   * @returns A sites url
   *
   * @example
   * const url = buildSitesUrl()
   * @example
   * const url = buildSitesUrl("overview")
   */
  buildSitesUrl: (tab?: string) => string

  /**
   * The root admin path taking into account the active organization
   */
  adminPath: string

  /**
   * The admin bills path (organization bills list)
   */
  adminBillsPath: string

  /**
   * Builds an admin bill show path for the given bill id
   */
  adminBillPath: (billId: string) => string

  /**
   * Builds an admin bill edit path for the given bill id
   */
  adminBillEditPath: (billId: string) => string

  /**
   * The admin meters path (organization meters grid)
   */
  adminMetersPath: string

  /**
   * Builds an admin meter show path for the given meter id
   */
  adminMeterPath: (meterId: string) => string

  /**
   * Builds an admin meter edit path for the given meter id
   */
  adminMeterEditPath: (meterId: string) => string

  /**
   * The admin sites path (organization sites grid)
   */
  adminSitesPath: string

  /**
   * Builds an admin site show path for the given site id
   */
  adminSitePath: (siteId: string) => string

  /**
   * Builds an admin site edit path for the given site id
   */
  adminSiteEditPath: (siteId: string) => string

  /**
   * The admin users path (organization users grid)
   */
  adminUsersPath: string

  /**
   * Builds an admin user show path for the given user id
   */
  adminUserPath: (userId: string) => string

  /**
   * Builds an admin user edit path for the given user id
   */
  adminUserEditPath: (userId: string) => string

  /**
   * The admin users create path
   */
  adminUsersNewPath: string

  /**
   * The admin credentials path (organization credentials page)
   */
  adminCredentialsPath: string

  /**
   * Builds an admin credential show path for the given credential id
   */
  adminCredentialPath: (credentialId: string) => string

  /**
   * Builds an admin credential edit path for the given credential id
   */
  adminCredentialEditPath: (credentialId: string) => string

  /**
   * The admin credentials create path
   */
  adminCredentialsNewPath: string

  /**
   * The root data path taking into account the active organization
   */
  dataPath: string

  /**
   * The root financial path taking into account the active organization
   */
  financialPath: string

  /**
   * The root labs path taking into account the active organization
   */
  labsPath: string

  /**
   * The root reporting path taking into account the active organization
   */
  reportingPath: string

  /**
   * The root sites path taking into account the active organization
   */
  sitesPath: string

  /**
   * The root user settings path taking into account the active organization
   */
  userSettingsPath: string
}

/**
 * Provides url paths and utility functions for the application
 *
 * @returns The paths and functions
 * @example
 * const {
 *   buildSiteUrl,
 *   buildSitesUrl,
 * } = useUrl()
 */
export const useUrl = (): UseUrlValue => {
  const { activeOrganizationId } = useActiveOrganizationId()

  const adminPath = getAuthenticatedRoutePath(
    activeOrganizationId,
    RootPath.Admin
  )

  const adminMetersPath = `${adminPath}/${AdminPath.Meters}`
  const adminBillsPath = `${adminPath}/${AdminPath.Bills}`
  const adminSitesPath = `${adminPath}/${AdminPath.Sites}`
  const adminCredentialsPath = `${adminPath}/${AdminPath.Credentials}`
  const adminUsersPath = `${adminPath}/${AdminPath.Users}`

  const dataPath = getAuthenticatedRoutePath(
    activeOrganizationId,
    RootPath.Data
  )

  const financialPath = getAuthenticatedRoutePath(
    activeOrganizationId,
    RootPath.Financial
  )

  const labsPath = getAuthenticatedRoutePath(
    activeOrganizationId,
    RootPath.Labs
  )

  const reportingPath = getAuthenticatedRoutePath(
    activeOrganizationId,
    RootPath.Report
  )

  const sitesPath = getAuthenticatedRoutePath(
    activeOrganizationId,
    RootPath.Site
  )

  const userSettingsPath = getAuthenticatedRoutePath(
    activeOrganizationId,
    RootPath.Settings
  )

  const buildReportingUrl = useCallback(
    (report: string) => {
      return `${reportingPath}/${report}`
    },
    [reportingPath]
  )

  const buildSiteUrl = useCallback(
    (siteId: string, tab?: string) => {
      if (tab) {
        return `${sitesPath}/${siteId}/${tab}`
      }

      return `${sitesPath}/${siteId}`
    },
    [sitesPath]
  )

  const buildSitesUrl = useCallback(
    (tab?: string) => {
      if (tab) {
        return `${sitesPath}/${tab}`
      }

      return sitesPath
    },
    [sitesPath]
  )

  const adminBillPath = useCallback(
    (billId: string) => `${adminBillsPath}/${billId}`,
    [adminBillsPath]
  )

  const adminBillEditPath = useCallback(
    (billId: string) => `${adminBillsPath}/${billId}/${AdminPath.Edit}`,
    [adminBillsPath]
  )

  const adminMeterPath = useCallback(
    (meterId: string) => `${adminMetersPath}/${meterId}`,
    [adminMetersPath]
  )

  const adminMeterEditPath = useCallback(
    (meterId: string) => `${adminMetersPath}/${meterId}/${AdminPath.Edit}`,
    [adminMetersPath]
  )

  const adminSitePath = useCallback(
    (siteId: string) => `${adminSitesPath}/${siteId}`,
    [adminSitesPath]
  )

  const adminSiteEditPath = useCallback(
    (siteId: string) => `${adminSitesPath}/${siteId}/${AdminPath.Edit}`,
    [adminSitesPath]
  )

  const adminUserPath = useCallback(
    (userId: string) => `${adminUsersPath}/${userId}`,
    [adminUsersPath]
  )

  const adminUserEditPath = useCallback(
    (userId: string) => `${adminUsersPath}/${userId}/${AdminPath.Edit}`,
    [adminUsersPath]
  )

  const adminUsersNewPath = `${adminUsersPath}/${AdminPath.New}`

  const adminCredentialPath = useCallback(
    (credentialId: string) => `${adminCredentialsPath}/${credentialId}`,
    [adminCredentialsPath]
  )

  const adminCredentialEditPath = useCallback(
    (credentialId: string) =>
      `${adminCredentialsPath}/${credentialId}/${AdminPath.Edit}`,
    [adminCredentialsPath]
  )

  const adminCredentialsNewPath = `${adminCredentialsPath}/${AdminPath.New}`

  return {
    buildReportingUrl,
    buildSiteUrl,
    buildSitesUrl,
    adminPath,
    adminBillPath,
    adminBillEditPath,
    adminBillsPath,
    adminMeterPath,
    adminMeterEditPath,
    adminMetersPath,
    adminSitePath,
    adminSiteEditPath,
    adminSitesPath,
    adminCredentialPath,
    adminCredentialEditPath,
    adminCredentialsPath,
    adminCredentialsNewPath,
    adminUsersPath,
    adminUserPath,
    adminUserEditPath,
    adminUsersNewPath,
    dataPath,
    financialPath,
    labsPath,
    reportingPath,
    sitesPath,
    userSettingsPath,
  }
}
